import { createContext, useContext } from 'react';
import { T } from './theme';

// Opening a booking from wherever it is mentioned.
//
// The same idea as ProfileViewerContext in profile.js: a booking reference is
// shown in half a dozen places — the dashboard's recent list, a person's
// history, a report — and in all of them it used to be dead text you had to
// copy and paste into the Bookings tab's search box.
//
// The context is kept in its own file on purpose. The thread it opens lives in
// panels/Bookings.js, which imports UserLink from profile.js; putting the
// context there too would have every panel importing the panel that imports
// them back.

export const BookingViewerContext = createContext(null);

export const useBookingViewer = () => useContext(BookingViewerContext) || (() => {});

/**
 * A booking reference, as a link to its chat.
 *
 * Falls back to plain text when there is no id to open — old reports carry a
 * reference string without one, and a button that cannot do anything is worse
 * than a label.
 */
export function BookingLink({
  bookingId,
  label,
  fallback = 'Booking',
  color = T.textMuted,
  weight = 700,
  size = 11.5,
}) {
  const open = useBookingViewer();
  const text = label || fallback;
  if (!bookingId) {
    return <span style={{ color, fontWeight: weight, fontSize: size, letterSpacing: 0.3 }}>{text}</span>;
  }
  return (
    <button
      className="ui-userlink"
      // Rows in Bookings, Reports and the dashboard are themselves clickable,
      // so this must not also trigger whatever is behind it.
      onClick={(e) => { e.stopPropagation(); open(bookingId); }}
      title="Open this booking"
      style={{
        background: 'none', border: 'none', padding: 0, margin: 0,
        font: 'inherit', fontSize: size, fontWeight: weight, color,
        letterSpacing: 0.3, cursor: 'pointer', textAlign: 'left',
        textDecoration: 'underline', textDecorationStyle: 'dotted',
        textUnderlineOffset: 3, textDecorationColor: 'currentColor',
      }}
    >{text}</button>
  );
}
